/**
 * ActiveJobsTray
 * ──────────────
 * Top-bar dropdown that shows in-flight video / image generation jobs
 * across the entire platform. Clicking a job navigates to the originating
 * surface (Media Studio for `media-studio-*`, Scene Composer, Avatar
 * Creator) so the user can keep watching its progress.
 *
 * Polls /social-hub/dashboard.active_jobs every 6s while jobs are active.
 * Idle (no jobs) → polls every 30s so we don't hammer the API.
 *
 * Sprint 888 — P1-C from VIDEO_GENERATION_AUDIT.md.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Activity } from "lucide-react";
import { api } from "../lib/api";
import { useLanguage } from "../contexts/LanguageContext";

const ACTIVE_POLL_MS = 6000;
const IDLE_POLL_MS = 30000;

const SOURCE_ROUTES = {
  "media-studio-t2v": "/marketing/social-hub?tab=media-studio",
  "media-studio-i2v": "/marketing/social-hub?tab=media-studio",
  "transition-stack": "/marketing/social-hub?tab=media-studio",
  "transition-timeline": "/marketing/social-hub?tab=media-studio",
  "brand-composer": "/marketing/social-hub?tab=brand-composer",
  "avatar_creator": "/creative-hub?tab=avatars",
  "website-builder": "/marketing/website",
  "proposal-builder": "/proposal-builder",
};

function jobLabel(j, t) {
  const type = (j.type || "").replace(/_/g, " ");
  return type || t("Generation");
}

function StatusIcon({ status }) {
  if (status === "processing" || status === "generating") return <Loader2 className="w-3.5 h-3.5 animate-spin text-pink-400" />;
  if (status === "completed" || status === "complete") return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />;
  if (status === "failed") return <XCircle className="w-3.5 h-3.5 text-red-400" />;
  return <Activity className="w-3.5 h-3.5 text-amber-400" />;
}

export function ActiveJobsTray() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);

  const fetchJobs = async () => {
    try {
      const r = await api.get("/social-hub/dashboard");
      const all = r.data?.active_jobs || [];
      // Show in-flight + freshly-failed-or-completed (last hour) so users
      // notice failures from any page.
      const cutoff = Date.now() - 60 * 60 * 1000;
      const visible = all.filter(j => {
        if (j.status === "processing" || j.status === "generating") return true;
        const t = j.completed_at || j.created_at;
        return t && new Date(t).getTime() > cutoff;
      });
      setJobs(visible);
    } catch { /* network blip — keep last set */ }
  };

  // Adaptive polling — busy when active jobs exist, idle otherwise.
  useEffect(() => {
    fetchJobs();
    const tick = () => {
      const hasActive = jobs.some(j => j.status === "processing" || j.status === "generating");
      timerRef.current = setTimeout(async () => {
        await fetchJobs();
        tick();
      }, hasActive ? ACTIVE_POLL_MS : IDLE_POLL_MS);
    };
    tick();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [jobs.length, jobs.some(j => j.status === "processing" || j.status === "generating")]); // eslint-disable-line

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const activeCount = jobs.filter(j => j.status === "processing" || j.status === "generating").length;
  const failedCount = jobs.filter(j => j.status === "failed").length;

  if (jobs.length === 0) return null;

  const onJobClick = (j) => {
    const route = SOURCE_ROUTES[j.source] || "/marketing/social-hub";
    setOpen(false);
    navigate(route);
  };

  const retryJob = async (e, jobId) => {
    e.stopPropagation();
    try {
      await api.post(`/social-hub/jobs/${jobId}/retry`);
      await fetchJobs();
    } catch { /* toast handled at LibraryTab level */ }
  };

  return (
    <div className="relative" ref={wrapRef} data-testid="active-jobs-tray">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-xl hover:bg-[var(--app-surface)] transition-colors flex items-center gap-1.5"
        data-testid="active-jobs-tray-toggle"
        title={t("Active generations")}
      >
        {activeCount > 0 ? (
          <Loader2 className="w-5 h-5 text-pink-400 animate-spin" />
        ) : (
          <Activity className="w-5 h-5 text-[var(--app-text-muted)]" />
        )}
        {activeCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center bg-pink-500 text-white" data-testid="active-jobs-tray-count">
            {activeCount}
          </span>
        )}
        {activeCount === 0 && failedCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center bg-red-500 text-white" data-testid="active-jobs-tray-failed-count">
            {failedCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-y-auto rounded-xl border border-[var(--app-border)] bg-[var(--app-surface)] shadow-xl z-50"
          data-testid="active-jobs-tray-panel"
        >
          <div className="px-3 py-2 border-b border-[var(--app-border)] flex items-center justify-between">
            <span className="text-xs font-bold text-[var(--app-text)] uppercase tracking-wider">{t("Active Generations")}</span>
            <span className="text-[10px] text-[var(--app-text-muted)]">{jobs.length} {t("recent")}</span>
          </div>
          <div className="divide-y divide-[var(--app-border)]">
            {jobs.map(j => (
              <button
                key={j.job_id}
                onClick={() => onJobClick(j)}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[var(--app-bg)] text-left transition-colors"
                data-testid={`active-jobs-tray-item-${j.job_id}`}
              >
                <StatusIcon status={j.status} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-[var(--app-text)] truncate">{jobLabel(j, t)}</div>
                  <div className="text-[10px] text-[var(--app-text-muted)] truncate">{(j.prompt || "").slice(0, 60) || j.source || "—"}</div>
                  {j.error && <div className="text-[10px] text-red-400 truncate mt-0.5">{j.error.slice(0, 80)}</div>}
                </div>
                {j.status === "failed" && (
                  <button
                    onClick={(e) => retryJob(e, j.job_id)}
                    className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500 text-white hover:bg-emerald-600 transition-colors"
                    data-testid={`active-jobs-tray-retry-${j.job_id}`}
                  >
                    {t("Retry")}
                  </button>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ActiveJobsTray;
